import React, { useState, useEffect } from 'react';
import {
  Gift, Plus, Edit2, Trash2, Check, AlertCircle, Loader, X,
  ToggleLeft, ToggleRight
} from 'lucide-react';
import {
  getLoyaltyRewards, createLoyaltyReward, updateLoyaltyReward, deleteLoyaltyReward
} from '../../services/api';
import '../Boutiques.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);
const EMPTY = { nom: '', description: '', coutPoints: 100, type: 'percentage', valeur: 10, actif: true };
const reducLabel = (r) => r.type === 'percentage' ? `-${r.valeur}%` : `-${fmt(r.valeur)} XOF`;

export default function LoyaltyRewards() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState({ type: '', text: '' });
  const [modal,   setModal]   = useState(false);
  const [edit,    setEdit]    = useState(null);
  const [form,    setForm]    = useState(EMPTY);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => { load(); }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 4000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getLoyaltyRewards();
      setRewards(data.data || []);
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur de chargement');
    } finally { setLoading(false); }
  };

  const openCreate = () => { setEdit(null); setForm(EMPTY); setModal(true); };
  const openEdit = (r) => {
    setEdit(r);
    setForm({ nom: r.nom, description: r.description || '', coutPoints: r.coutPoints, type: r.type || 'percentage', valeur: r.valeur, actif: r.actif });
    setModal(true);
  };
  const close = () => setModal(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) { showToast('error', 'Le nom est requis'); return; }
    if (!(form.coutPoints > 0)) { showToast('error', 'Le coût doit être > 0'); return; }
    if (!(Number(form.valeur) > 0)) { showToast('error', 'La valeur de réduction doit être > 0'); return; }
    if (form.type === 'percentage' && Number(form.valeur) > 100) { showToast('error', 'Le pourcentage ne peut dépasser 100'); return; }
    setSaving(true);
    try {
      const payload = { ...form, coutPoints: parseInt(form.coutPoints, 10), valeur: Number(form.valeur) };
      if (edit) await updateLoyaltyReward(edit.id, payload);
      else await createLoyaltyReward(payload);
      showToast('success', edit ? 'Récompense modifiée' : 'Récompense créée');
      close(); load();
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Erreur');
    } finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try { await updateLoyaltyReward(r.id, { actif: !r.actif }); load(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer la récompense « ${r.nom} » ?`)) return;
    try { await deleteLoyaltyReward(r.id); showToast('success', 'Récompense supprimée'); load(); }
    catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
  };

  if (loading) return (
    <div className="bt-loading"><Loader className="bt-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="bt-page">
      {toast.text && (
        <div className={`bt-toast bt-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={14}/> : <AlertCircle size={14}/>}
          {toast.text}
        </div>
      )}

      <div className="bt-header">
        <div className="bt-header-left">
          <div className="bt-header-icon"><Gift size={20}/></div>
          <div>
            <h1 className="bt-header-title">Récompenses</h1>
            <p className="bt-header-sub">Catalogue des récompenses échangeables contre des points</p>
          </div>
        </div>
        <button className="bt-cta-btn" onClick={openCreate}><Plus size={14}/> Nouvelle récompense</button>
      </div>

      <div className="bt-content">
        {rewards.length === 0 ? (
          <div className="bt-empty">
            <div className="bt-empty-icon"><Gift size={30}/></div>
            <h3>Aucune récompense</h3>
            <p>Créez vos premières récompenses (ex: « -10% », « Café offert »).</p>
            <button className="bt-cta-btn" onClick={openCreate}><Plus size={13}/> Créer une récompense</button>
          </div>
        ) : (
          <div className="bt-card">
            <table className="bt-table">
              <thead>
                <tr><th>Récompense</th><th>Réduction</th><th>Coût</th><th>Statut</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {rewards.map(r => (
                  <tr key={r.id} className={!r.actif ? 'bt-row--dim' : ''}>
                    <td>
                      <div className="bt-cell-name">{r.nom}</div>
                      {r.description && <div className="bt-cell-sub">{r.description}</div>}
                    </td>
                    <td><span className="bt-badge" style={{ background: '#eef2ff', color: '#4338ca' }}>{reducLabel(r)}</span></td>
                    <td><strong>{fmt(r.coutPoints)}</strong> pts</td>
                    <td>
                      <span className={`bt-badge ${r.actif ? 'bt-badge--active' : 'bt-badge--inactive'}`}>
                        {r.actif ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="bt-actions">
                        <button className="bt-action-btn bt-action-btn--edit" onClick={() => openEdit(r)} title="Modifier">
                          <Edit2 size={12}/> Modifier
                        </button>
                        <button className={`bt-ico-btn ${r.actif ? '' : 'bt-ico-btn--on'}`}
                          onClick={() => toggle(r)} title={r.actif ? 'Désactiver' : 'Activer'}>
                          {r.actif ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
                        </button>
                        <button className="bt-ico-btn bt-ico-btn--del" onClick={() => remove(r)} title="Supprimer">
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <>
          <div className="bt-overlay" onClick={close}/>
          <div className="bt-modal">
            <div className="bt-modal-head">
              <div>
                <h2 className="bt-modal-title">{edit ? 'Modifier la récompense' : 'Nouvelle récompense'}</h2>
              </div>
              <button className="bt-modal-close" onClick={close}><X size={16}/></button>
            </div>
            <form onSubmit={submit} className="bt-modal-body">
              <div className="bt-field">
                <label>Nom *</label>
                <input type="text" value={form.nom} autoFocus required
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                  placeholder="Ex: -10% sur la prochaine commande"/>
              </div>
              <div className="bt-field">
                <label>Description</label>
                <textarea rows={2} value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Détails / conditions (optionnel)"/>
              </div>
              <div className="bt-form-row">
                <div className="bt-field">
                  <label>Type de réduction *</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (XOF)</option>
                  </select>
                </div>
                <div className="bt-field">
                  <label>{form.type === 'percentage' ? 'Pourcentage *' : 'Montant (XOF) *'}</label>
                  <input type="number" min="1" max={form.type === 'percentage' ? 100 : undefined} required
                    value={form.valeur} onChange={e => setForm({ ...form, valeur: e.target.value })}/>
                </div>
              </div>
              <div className="bt-field">
                <label>Coût en points *</label>
                <input type="number" min="1" required value={form.coutPoints}
                  onChange={e => setForm({ ...form, coutPoints: e.target.value })}/>
              </div>
              <div className="bt-modal-footer">
                <button type="button" className="bt-btn-cancel" onClick={close}>Annuler</button>
                <button type="submit" className="bt-btn-confirm" disabled={saving}>
                  {saving ? <><Loader size={13} className="bt-spin"/> Enregistrement…</> : <><Check size={13}/> {edit ? 'Enregistrer' : 'Créer'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
